import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls
} from 'reactflow';
import 'reactflow/dist/style.css';

import {
  fetchFlows,
  fetchGroups,
  fetchRollen,
  fetchTasks,
  getTemplateDict
} from '../functions.js/airTableDB';


import { translateFromRawTamplateToStaticJson, funcHandleAddNode} from '../functions.js/workflowBuilderHelpers';

import {
  funcHandleNodeDragStart,
  funcHandleNodeDrag,
  funcHandleNodeDragStop,


  funcHandleDeleteNode,
  funcHandleEditBlock,
  funcHandleConnection,
  funcCutChildrenEdges,
  funcHandleEdgeClick,
} from '../functions.js/handlersCallbacks';


import { GroupNode, TaskNode } from './workflowBuilderSuperNodes';
import { EditNodePopup, PopupToAddNewNodes } from './popup.js';
import { transformFromStaticToNodesAndEdges } from '../functions.js/transformStaticToNodes';



window.WORKFLOWBUILDER_CHILD_WIDTH=300
window.WORKFLOWBUILDER_CHILD_HEIGHT=150






export default function WorkflowBuilder({
  modus,           // "editGroup" / "editFlow"
  isTemplateEditing,
  templteRecID,
  savingFunc,
  closeChartFun
}) {

    // ==================== STATES =======================
    const [workspaceRoles, setWorkspaceRoles] = useState([]);
    const [aufgabenVorlagen, setAufgabenVorlagen] = useState([]);
    const [gruppenVorlagen, setGruppenVorlagen] = useState([]);
    const [vorlageRaw, setVorlageRaw] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(null);

    const [staticJSON, setStaticJSON] = useState(null);
    const [nodesAndEdges, setNodesAndEdges] = useState({ nodes: [], edges: [] });

    const [currentEdge, setCurrentEdge] = useState(null)
    const [isDragging, setIsDragging] = useState(false)

    // Bearbeitungszustände
    const [currentEditingGroupId, setCurrentEditingGroupId] = useState(null);

    const [popupPosition, setPopupPosition] = useState(null);
    const [editPopup, setEditPopup] = useState(null);


    const lastDragPositions = useRef({});

    // Node-Types
    const nodeTypesDict = {
        taskNode: TaskNode,
        groupNode: GroupNode
    };


    // ==================== HELPER =======================
    // Beispiel: Umschalten des Gruppen-Edit-Modus
    const handleEditGroup = useCallback((groupId) => {
        console.log("handleEditGroup: ",groupId," currentEditingGroupId:",currentEditingGroupId)
        if (currentEditingGroupId != groupId)
        {
            setCurrentEditingGroupId(groupId)
        }else if(currentEditingGroupId == groupId)
        {
            setCurrentEditingGroupId(null)
        }
    }, [staticJSON, currentEditingGroupId]);

    // ==================== HANDLER ======================
    const handleNodeDelete = useCallback((nodeId, parentId) => {
        funcHandleDeleteNode(nodeId, parentId, staticJSON, setStaticJSON);
    }, [staticJSON]);

    const handleEditNode = useCallback((nodeId, parentId, type, event) => {
      funcHandleEditBlock(nodeId, parentId, type, event, staticJSON, setEditPopup);
    }, [staticJSON]);

    const handleAddNode = useCallback((template, type) => {
        funcHandleAddNode(template, type, currentEditingGroupId, popupPosition, setStaticJSON, staticJSON, workspaceRoles, aufgabenVorlagen);
        setPopupPosition(null);
    }, [currentEditingGroupId, popupPosition, staticJSON]);

    const handleChildrenEdgeCut = useCallback((nodeId, parentId) => {
        console.log("handleChildrenEdgeCut: ",nodeId, parentId)
        funcCutChildrenEdges(nodeId, parentId, staticJSON,setStaticJSON);
    }, [staticJSON]);

    // Rechts‑Klick auf die Pane – Popup öffnen
    const handlePaneContextMenu = useCallback((event) => {
        console.log("Clicked - modus: ", modus, " currentEditingGroupId: ", currentEditingGroupId)
        if (modus == "editFlow" || (modus == "editGroup" && currentEditingGroupId)) { 
            event.preventDefault();
            const position = { x: event.clientX, y: event.clientY };
            setPopupPosition(position);
        }
    }, [modus, currentEditingGroupId]);
    
    const handleConnect = useCallback((connection) => {
        funcHandleConnection(currentEditingGroupId, connection.source, connection.target, setStaticJSON, staticJSON)
    }, [staticJSON, currentEditingGroupId]);

    const onEdgeClick = useCallback((event, edge) => {
      funcHandleEdgeClick(edge, staticJSON, setStaticJSON)
  }, [staticJSON, currentEdge]);


  const handlePopupSave = useCallback((formData) => {
    // Beispiel: formData = {
    //   nodeId,
    //   nodeType,      // "task" oder "group"
    //   parentId,
    //   name,
    //   duration_days,
    //   rolle,
    //   template_id
    // };

    // 1) Kopie des bestehenden staticJSON
    const updatedJSON = { ...staticJSON, blocks: [...staticJSON.blocks] };

    if (formData.nodeType === "group") {
      // Gruppe auf oberster Ebene suchen
      const groupBlock = updatedJSON.blocks.find(b => b.instance_id === formData.nodeId);
      if (groupBlock) {
        groupBlock.name = formData.name;
        // Falls weitere group-spezifische Felder zu ändern sind, hier ergänzen
      }
    }
    else if (formData.nodeType === "task") {
      // Task auf oberster Ebene suchen
      let foundBlock = updatedJSON.blocks.find(b => b.instance_id === formData.nodeId);

      if (foundBlock) {
        // Oberster Task
        foundBlock.name = formData.name;
        foundBlock.duration_days = formData.duration_days;
        foundBlock.rolle = formData.rolle;
        foundBlock.clickup_vorlage_id = formData.clickup_vorlage_id;
      } else {
        // Oder in children_blocks suchen
        for (let b of updatedJSON.blocks) {
          if (Array.isArray(b.children_blocks)) {
            let child = b.children_blocks.find(cb => cb.instance_id === formData.nodeId);
            if (child) {
              child.name = formData.name;
              child.duration_days = formData.duration_days;
              child.rolle = formData.rolle;
              child.clickup_vorlage_id = formData.clickup_vorlage_id;
              break;
            }
          }
        }
      }
    }

    // 2) State aktualisieren => Popup schließen
    setStaticJSON(updatedJSON);
    setEditPopup(null);
  }, [staticJSON]);

  const handlePopupClose = useCallback(() => {
    setEditPopup(null);
  }, []);


  // ==================== EFFECTS ======================
  // 1) Daten laden
  useEffect(() => {
    async function fetchData() {
      try {
        if (modus === "editGroup") {
          await Promise.all([
            fetchTasks(setAufgabenVorlagen),
            fetchRollen(setWorkspaceRoles),
            getTemplateDict(templteRecID, "groups", setVorlageRaw)
          ]);
        } else if (modus === "editFlow") {
          await Promise.all([
            fetchGroups(setGruppenVorlagen),
            fetchTasks(setAufgabenVorlagen),
            fetchRollen(setWorkspaceRoles),
            getTemplateDict(templteRecID, "flows", setVorlageRaw)
          ]);
        } else {
          throw new Error("Falscher Modus / VorlagenEditing");
        }
        setIsLoading(false);
      } catch (err) {
        setIsError(String(err));
      }
    }
    fetchData();
  }, [modus, templteRecID]);


  // 2) Zusammengepuzzeltes JSON
  useEffect(() => {
    if (!isLoading && vorlageRaw) {
      const combined = translateFromRawTamplateToStaticJson(
        modus,
        vorlageRaw,
        aufgabenVorlagen,
        gruppenVorlagen,
        workspaceRoles
      );
      setStaticJSON(combined);
    }
  }, [isLoading, vorlageRaw, modus, aufgabenVorlagen, gruppenVorlagen, workspaceRoles]);


  // 3) Aus staticJSON => Nodes & Edges
  useEffect(() => {
    if (staticJSON) {
      // transformFromStaticToNodesAndEdges kann 
      // optional Parameter annehmen, aber hier z.B. nur (staticJSON)
      const { nodes, edges } = transformFromStaticToNodesAndEdges(staticJSON, modus, isTemplateEditing, handleEditGroup, handleEditNode, handleNodeDelete, handleChildrenEdgeCut, currentEditingGroupId, workspaceRoles, isDragging);
      setNodesAndEdges({ nodes, edges });
    }
  }, [staticJSON, currentEditingGroupId, isDragging]);




  // ==================== JSX: Render ==================
  if (isError) {
    return <div id="builder-mainbox"><p>Error: {isError}</p></div>;
  }
  if (isLoading) {
    return <div id="builder-mainbox"><p>Lade Daten...</p></div>;
  }

  console.log("Wir haben", nodesAndEdges.nodes.length, "Nodes und ", nodesAndEdges.edges.length ," Edges")
  return (
    <div id="builder-mainbox" style={{ width: '100%', height: '600px' }}>
        <div className="save-chart" onClick={() => savingFunc(templteRecID, modus, isTemplateEditing, staticJSON)}>
            speichern
        </div>

        <div className="close-chart" onClick={() => closeChartFun()}>
        ✖
        </div>

        <ReactFlow
            nodes={nodesAndEdges.nodes}
            edges={nodesAndEdges.edges}
            nodeTypes={nodeTypesDict}
            maxZoom={5}
            minZoom={0.25}
            snapToGrid={true}
            snapGrid={[48, 48]}
            onNodeDragStart={(e, n) => { setIsDragging(true); funcHandleNodeDragStart(e, n, lastDragPositions) }}
            onNodeDrag={(e, n) => funcHandleNodeDrag(e, n, lastDragPositions, setStaticJSON)}
            onNodeDragStop={(e, n) => { funcHandleNodeDragStop(e, n, lastDragPositions, setStaticJSON); setIsDragging(false); }}
            onPaneContextMenu={handlePaneContextMenu}
            onConnect={handleConnect}
            onEdgeClick={onEdgeClick}
            onEdgeMouseEnter={(e)=>setCurrentEdge(e)}
            onEdgeMouseLeave={(e)=>{setCurrentEdge(null)}}
            fitView
        >
            <Controls />
            <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
        {/* Popup zum Hinzufügen neuer Nodes */}
        {popupPosition && (
            <PopupToAddNewNodes
            groupTemplates={gruppenVorlagen}
            taskTemplates={aufgabenVorlagen}
            position={popupPosition}
            handleAddNode={handleAddNode}
            handlePopupClose={() => setPopupPosition(null)}
            currentEditingGroupId={currentEditingGroupId}
            />
        )}

        {/* Popup zum Editieren, etc. (Beispiel) */}
        {editPopup && (
        <EditNodePopup
          popupData={editPopup}
          onSave={handlePopupSave}
          onClose={handlePopupClose}
        />
      )}
    </div>
  );
}
